import { Checkbox, Field, IconButton, toast } from "@carbon6/c6-frontend-design";
import { useEffect, useId, useRef, useState, type ChangeEvent } from "react";
import { createDiagnosticJobs } from "@/api/adminDiagnosticJob";
import { adminOperationError } from "@/app/adminOperationError";
import { hatcheryTextModes } from "@/app/theme";
import { DatasetPicker } from "@/components/dataset/selection/DatasetPicker";
import { DatasetPreviewPanel } from "@/components/dataset/selection/DatasetPreviewPanel";
import type { DatasetSelection } from "@/components/dataset/selection/DatasetSelection";
import selectionStyles from "@/components/dataset/selection/DatasetSelection.module.css";
import { FullscreenWindow, HatTextModal, type WindowEntry } from "@/components/modal";
import { windowStack } from "@/app/windowStack";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { diagnosticJobsChangedEvent } from "./diagnosticSubmitWindow";
import styles from "./DiagnosticSubmitWindowContent.module.css";
import { storageBackendGateFor } from "@/api/datasetStorageBackend";

export function DiagnosticSubmitWindowContent({
  entry,
  active,
}: {
  entry: WindowEntry;
  active: boolean;
}) {
  const [datasets, setDatasets] = useState<DatasetSelection[]>([]);
  // 강제 재계산. 원본 카메라 영상 전체를 다시 디코드하므로 기본은 끈다.
  const [reset, setReset] = useState(false);
  const resetHintId = useId();
  const [preview, setPreview] = useState<DatasetSelection | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (previewTimer.current !== null) clearTimeout(previewTimer.current);
    };
  }, []);
  const close = () => windowStack.close(entry.id);
  const toggle = (selection: DatasetSelection) => {
    if (busy) return;
    setDatasets((current) =>
      current.some((item) => item.uuid === selection.uuid)
        ? current.filter((item) => item.uuid !== selection.uuid)
        : [...current, selection],
    );
  };
  const schedulePreview = (item: { uuid: string; name: string }) => {
    if (previewTimer.current !== null) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => setPreview({ uuid: item.uuid, label: item.name }), 200);
  };
  const submit = async () => {
    if (busy || datasets.length === 0 || datasets.length > 100) return;
    setBusy(true);
    setError(null);
    windowStack.update(entry.id, { closable: false });
    try {
      await createDiagnosticJobs(
        datasets.map((item) => item.uuid),
        reset,
      );
      if (alive.current) {
        window.dispatchEvent(new Event(diagnosticJobsChangedEvent));
        toast.success(
          `${datasets.length}개 Dataset의 ${reset ? "강제 재계산" : "재진단"}을 요청했습니다.`,
        );
        windowStack.update(entry.id, { closable: true });
        close();
      }
    } catch (caught) {
      if (alive.current) setError(adminOperationError(caught));
    } finally {
      if (alive.current) {
        setBusy(false);
        windowStack.update(entry.id, { closable: true });
      }
    }
  };
  const previewDataset = preview ?? datasets[0] ?? null;
  return (
    <>
      <FullscreenWindow
        open={active}
        title="진단 작업 추가"
        bodyLayout="fill"
        onMinimize={() => windowStack.minimize(entry.id)}
        onRequestClose={() => {
          if (!busy) {
            if (datasets.length > 0) setConfirmClose(true);
            else close();
          }
        }}
        footer={
          <div className={styles.footer}>
            <span>{datasets.length}개 Dataset 선택</span>
            {error && (
              <span role="alert" className={styles.error}>
                {error}
              </span>
            )}
            <IconButton
              text={busy ? "요청 중…" : "재진단 요청"}
              size="sm"
              variant="solid"
              disabled={busy || datasets.length === 0 || datasets.length > 100}
              onClick={() => void submit()}
            />
          </div>
        }
      >
        <div className={selectionStyles.datasetStep}>
          <div className={selectionStyles.datasetPickerCol}>
            <section className={styles.settings} aria-label="진단 설정">
              <h3>진단 설정</h3>
              <p>선택한 Dataset의 원본을 다시 진단합니다. 기존 진단 진행은 초기화됩니다.</p>
              <p>한 번에 최대 100개를 요청할 수 있습니다.</p>
              <div className={styles.reset}>
                <Field
                  label="강제 재계산"
                  layout="hug"
                  labelPlacement="end"
                  disabled={busy}
                  textVariant={hatcheryTextModes["body-normal"]}
                >
                  <Checkbox
                    checked={reset}
                    aria-describedby={resetHintId}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setReset(event.target.checked)
                    }
                  />
                </Field>
                <p id={resetHintId}>
                  제외하지 않은 invalid 판정을 pending으로 되돌리고, 원본 카메라 영상을 캐시 없이
                  모두 다시 디코드합니다. 결함 수용·코멘트·리뷰 판정은 바꾸지 않습니다.
                </p>
              </div>
            </section>
            <div className={selectionStyles.pickerPanel} aria-busy={busy}>
              <DatasetPicker
                selectedUuids={new Set(datasets.map((item) => item.uuid))}
                onToggle={toggle}
                onPreview={schedulePreview}
                countLabel={`${datasets.length}개 선택`}
                // 진단을 지원하지 않는 저장 위치의 Dataset 은 요청이 409 다. 그래서 후보에서 뺀다.
                storageBackend={storageBackendGateFor("diagnostics") ?? undefined}
              />
            </div>
            {datasets.length > 100 && (
              <p role="alert" className={styles.error}>
                Dataset을 100개 이하로 선택해 주세요.
              </p>
            )}
          </div>
          <div className={selectionStyles.datasetPreviewCol}>
            {previewDataset ? (
              <DatasetPreviewPanel key={previewDataset.uuid} selection={previewDataset} />
            ) : (
              <EmptyState size="lg">
                목록에서 Dataset을 클릭하면 정보·미리보기가 표시됩니다.
              </EmptyState>
            )}
          </div>
        </div>
      </FullscreenWindow>
      <HatTextModal
        open={confirmClose}
        label="진단 작업 추가 창을 닫으시겠습니까?"
        caption="선택한 Dataset과 진단 설정이 사라집니다."
        onOkPress={close}
        onCancelPress={() => setConfirmClose(false)}
      />
    </>
  );
}
